/**
 * @description AuthCallback component for handling OAuth callback redirects
 *
 * Key features:
 * - Extracts access token from URL fragment (#access_token=...)
 * - Stores token via AuthContext (proper state management)
 * - Fetches user info before navigation (eliminates race condition)
 * - Redirects to /channel-partners on success (admin portal default)
 * - Handles error states from failed auth
 *
 * @dependencies
 * - AuthContext: For handleAuthCallback to store token and fetch user
 *
 * @notes
 * - Backend redirects here after OAuth callback: /auth/callback#access_token=xxx&expires_in=xxx
 * - Token stored in URL fragment (not query params) for security
 * - handleAuthCallback ensures auth state is set BEFORE navigation
 * - Adapted from merchant frontend AuthCallback (redirects to /tenants instead of /dashboard)
 */

import { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { ADMIN_DEFAULT_PATH } from '../../utils/adminNavActive';

function parseHashParams(hash) {
  if (!hash) return {};

  const params = {};
  const pairs = hash.split('&');

  for (const pair of pairs) {
    const [key, value] = pair.split('=');
    if (key) {
      params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
    }
  }

  return params;
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleAuthCallback } = useContext(AuthContext);

  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Check for error in query params (from failed OAuth)
        const searchParams = new URLSearchParams(location.search);
        const queryError = searchParams.get('error');

        if (queryError) {
          const errorDescription = searchParams.get('error_description') || 'Authentication failed';
          setError(getErrorMessage(queryError, errorDescription));
          return;
        }

        // Parse access token from URL fragment
        const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
        const hashParams = parseHashParams(hash);

        const accessToken = hashParams.access_token;
        const expiresIn = hashParams.expires_in;

        if (!accessToken) {
          setError('No access token received. Please try signing in again.');
          return;
        }

        // Clear the URL hash for security
        window.history.replaceState(null, '', location.pathname);

        const success = await handleAuthCallback(accessToken, parseInt(expiresIn, 10) || 3600);

        if (!success) {
          setError('Access denied. This portal requires administrator privileges.');
          return;
        }

        // Auth successful — Channel Partners until a dashboard exists
        navigate(ADMIN_DEFAULT_PATH, { replace: true });
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('An unexpected error occurred. Please try signing in again.');
      }
    };

    processCallback();
  }, [location, navigate, handleAuthCallback]);

  function getErrorMessage(errorCode, errorDescription) {
    const errorMessages = {
      'access_denied': 'Access was denied. Please try signing in again.',
      'invalid_request': 'Invalid request. Please try signing in again.',
      'invalid_state': 'Session expired. Please try signing in again.',
      'invalid_callback': 'Invalid callback. Please try signing in again.',
      'session_expired': 'Your session has expired. Please sign in again.',
      'callback_failed': 'Authentication failed. Please try again.',
      'login_required': 'Please sign in to continue.',
    };

    return errorMessages[errorCode] || errorDescription || 'Authentication failed. Please try again.';
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6 mx-auto">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Authentication failed</h1>
          <p className="text-slate-400 text-sm mb-8">{error}</p>
          <Link
            to="/sign-in"
            className="inline-flex items-center justify-center w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-indigo-500"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  // Loading state while processing
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        <p className="text-sm text-slate-400">Signing you in...</p>
      </div>
    </div>
  );
}

export default AuthCallback;
